// redux/slices/businessLocationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const normalizeBusinessLocationResponse = (response) => {
  const data = response?.data ?? response ?? {};

  return (
    data?.businessLocation ||
    data?.data?.businessLocation ||
    data
  );
};

export const fetchBusinessLocation = createAsyncThunk(
  "businessLocation/fetchBusinessLocation",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "GET",
        `/business-locations/${uid}`
      );

      return normalizeBusinessLocationResponse(response);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(
          error,
          "Ошибка при получении бизнес-локации"
        )
      );
    }
  }
);

export const createBusinessLocation = createAsyncThunk(
  "businessLocation/updateBusinessLocation",
  async (
    {
      address,
      city,
      businessName,
      uid,
      aboutBusiness,
      aboutBusinessHe,
      aboutBusinessEn,
      receiptName,
    },
    { rejectWithValue }
  ) => {
    try {
      if (!uid) throw new Error("UID не передан");

      if (!address || !city || !businessName) {
        throw new Error(
          "Отсутствуют обязательные данные для создания бизнес-локации"
        );
      }

      const response = await apiRequest(
        "POST",
        "/business-locations",
        {
          address,
          city,
          businessName,
          uid,
          aboutBusiness,
          aboutBusinessHe,
          aboutBusinessEn,
          receiptName,
        }
      );

      return normalizeBusinessLocationResponse(response);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(
          error,
          "Ошибка при создании/обновлении бизнес-локации"
        )
      );
    }
  }
);

export const fetchBusinessLocationByUid = createAsyncThunk(
  "businessLocation/fetchBusinessLocationByUid",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "GET",
        `/business-locations/${uid}`
      );

      return {
        uid: String(uid),
        businessLocation:
          normalizeBusinessLocationResponse(response),
      };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(
          error,
          "Ошибка при получении бизнес-локации по UID"
        )
      );
    }
  }
);

export const fetchPublicBusinessLocation = createAsyncThunk(
  "businessLocation/fetchPublicBusinessLocation",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const base = getServerUrl().replace(/\/$/, "");
      const { data } = await axios.get(
        `${base}/public/${encodeURIComponent(
          uid
        )}/business-location`
      );

      return {
        uid: String(uid),
        businessLocation:
          data?.businessLocation ?? data ?? {},
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to fetch public business location"
      );
    }
  }
);

export const selectBusinessLocationByUid = (state, uid) =>
  state?.businessLocation?.businessLocationsByUid?.[
    String(uid)
  ] || null;

const initialState = {
  address: "",
  city: "",
  businessName: "",
  aboutBusiness: "",
  aboutBusinessHe: "",
  aboutBusinessEn: "",
  receiptName: "",
  uid: "",

  status: "idle",
  error: null,

  businessLocationsByUid: {},
};

const applyLocation = (state, location = {}) => {
  state.address = location.address || "";
  state.city = location.city || "";
  state.businessName = location.businessName || "";
  state.aboutBusiness = location.aboutBusiness || "";
  state.aboutBusinessHe = location.aboutBusinessHe || "";
  state.aboutBusinessEn = location.aboutBusinessEn || "";
  state.receiptName = location.receiptName || "";
  state.uid = location.uid || state.uid || "";
};

const businessLocationSlice = createSlice({
  name: "businessLocation",
  initialState,
  reducers: {
    clearBusinessLocationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessLocation.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchBusinessLocation.fulfilled,
        (state, action) => {
          state.status = "succeeded";
          state.error = null;
          applyLocation(state, action.payload);
        }
      )
      .addCase(
        fetchBusinessLocation.rejected,
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload ||
            action.error?.message ||
            "Failed to fetch business location";
        }
      )

      .addCase(createBusinessLocation.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        createBusinessLocation.fulfilled,
        (state, action) => {
          state.status = "succeeded";
          state.error = null;
          applyLocation(state, action.payload);
        }
      )
      .addCase(
        createBusinessLocation.rejected,
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload ||
            action.error?.message ||
            "Failed to save business location";
        }
      )

      .addCase(
        fetchBusinessLocationByUid.pending,
        (state) => {
          state.status = "loading";
          state.error = null;
        }
      )
      .addCase(
        fetchBusinessLocationByUid.fulfilled,
        (state, action) => {
          const { uid, businessLocation } =
            action.payload || {};

          if (uid) {
            state.businessLocationsByUid[uid] =
              businessLocation || {};
          }

          state.status = "succeeded";
          state.error = null;
        }
      )
      .addCase(
        fetchBusinessLocationByUid.rejected,
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload ||
            action.error?.message;
        }
      )

      .addCase(
        fetchPublicBusinessLocation.pending,
        (state) => {
          state.status = "loading";
          state.error = null;
        }
      )
      .addCase(
        fetchPublicBusinessLocation.fulfilled,
        (state, action) => {
          const { uid, businessLocation } =
            action.payload || {};

          if (uid) {
            state.businessLocationsByUid[uid] =
              businessLocation || {};
          }

          state.status = "succeeded";
          state.error = null;
        }
      )
      .addCase(
        fetchPublicBusinessLocation.rejected,
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload ||
            action.error?.message ||
            "Failed to fetch public business location";
        }
      )

      .addCase(resetStore, () => initialState);
  },
});

export const { clearBusinessLocationError } =
  businessLocationSlice.actions;

export default businessLocationSlice.reducer;
